// Ultimate Card Game (UCG) - Economy & Resource Engine
//
// Neustart "Marine Null": plasma is the only resource. Minerals, CO
// Power and the five tech-upgrade lines are gone - see
// NEUSTART_PLAN.html Phase 2. The tech-upgrade lines were dead weight
// even before this: only the AI ever bought them (js/engine/ai.js) and no
// code anywhere read economy.resources[team].upgrades back out.

import { BALANCE } from '../data/balance.js';

export class EconomyEngine {
  constructor(battlefield = null) {
    this.battlefield = battlefield;
    this.stats = null;
    this.resources = {
      western: this._makeResources(),
      eastern: this._makeResources()
    };
  }

  _makeResources() {
    return {
      plasma: BALANCE.startPlasma,
      maxPlasma: BALANCE.maxPlasma,
      plasmaRate: BALANCE.plasmaRate,
      // Territorial control bonus multiplier (js/engine/battlefield.js's
      // updateTerritorialControl()) - 1.0 with no advance, up to
      // 1 + BALANCE.territoryMaxBonus deep in enemy territory.
      plasmaMultiplier: 1.0,
      // Flat income factor for this side. Only ever != 1 for an offline bot
      // whose difficulty declares an income bonus (BotAI.applyIncomeMultiplier(),
      // js/engine/ai/profiles.js) - the online server never sets it.
      incomeMultiplier: 1.0
    };
  }

  reset() {
    this.resources.western = this._makeResources();
    this.resources.eastern = this._makeResources();
    if (this.is2v2) {
      this.resources.w1 = this._makeResources();
      this.resources.w2 = this._makeResources();
      this.resources.e1 = this._makeResources();
      this.resources.e2 = this._makeResources();
    }
  }

  init2v2() {
    this.is2v2 = true;
    this.resources.w1 = this._makeResources();
    this.resources.w2 = this._makeResources();
    this.resources.e1 = this._makeResources();
    this.resources.e2 = this._makeResources();
  }

  update(dt) {
    const keys = this.is2v2 ? ['w1', 'w2', 'e1', 'e2'] : ['western', 'eastern'];
    for (const key of keys) {
      const res = this.resources[key];
      if (!res) continue;
      const team = (key === 'w1' || key === 'w2' || key === 'western') ? 'western' : 'eastern';
      if (this.resources[team]) {
        res.plasmaMultiplier = this.resources[team].plasmaMultiplier;
      }
      let rate = res.plasmaRate;
      if (this.battlefield?.moduleSystem?.hasCongressBuff(team, 'tariffs')) {
        rate += 0.5;
      }
      const mined = rate * res.plasmaMultiplier * (res.incomeMultiplier ?? 1) * dt;
      if (res.plasma + mined >= res.maxPlasma) {
        this.stats?.recordPlasmaCapped?.(team);
      }
      res.plasma = Math.min(res.maxPlasma, res.plasma + mined);
      this.stats?.recordPlasmaHarvested(team, mined);
    }
  }

  canAffordCard(teamOrRole, cost) {
    return (this.resources[teamOrRole]?.plasma ?? 0) >= cost;
  }

  spendPlasma(teamOrRole, cost) {
    if (this.canAffordCard(teamOrRole, cost)) {
      this.resources[teamOrRole].plasma -= cost;
      const team = (teamOrRole === 'w1' || teamOrRole === 'w2' || teamOrRole === 'western') ? 'western' : 'eastern';
      this.stats?.recordPlasmaSpent(team, cost);
      return true;
    }
    return false;
  }

  addPlasma(teamOrRole, amount) {
    const res = this.resources[teamOrRole];
    if (!res || amount <= 0) return 0;
    if (res.plasma + amount >= res.maxPlasma) {
      const team = (teamOrRole === 'w1' || teamOrRole === 'w2' || teamOrRole === 'western') ? 'western' : 'eastern';
      this.stats?.recordPlasmaCapped?.(team);
    }
    const gained = Math.min(res.maxPlasma - res.plasma, amount);
    res.plasma = Math.min(res.maxPlasma, res.plasma + amount);
    const team = (teamOrRole === 'w1' || teamOrRole === 'w2' || teamOrRole === 'western') ? 'western' : 'eastern';
    this.stats?.recordPlasmaHarvested(team, gained);
    return gained;
  }
}
